import React, {useEffect, useRef, useState} from "react";
import {Box, Button, CardActions, CardContent, InputAdornment, TextField, Typography} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import BalanceNumericFormat from "../utils/BalanceNumericFormat";
import {deleteSetBalance, saveSetBalance} from "../../store/transactionSlice";
import DateService from "../../services/DateService";
import {Delete} from "@mui/icons-material";
import { selectWalletById } from "../../store/walletSlice";
import { selectCurrencies } from "../../store/currencySlice";
import ViewAndEditFormLayout from "./ViewAndEditFormLayout";
import {useTranslation} from "react-i18next";


const EditSetBalanceForm = ({setBalance, onCancel, resetTrigger}) => {
    const {t} = useTranslation();
    const DEFAULT_ERROR = {wallet: null, amount: null, date: null, time: null};

    const dispatch = useDispatch();
    const [transactionDate, setTransactionDate] = useState(DateService.getTodayLocalDateString());
    const [transactionTime, setTransactionTime] = useState("07:30");
    const [description, setDescription] = useState(setBalance.description);
    const [walletId, setWalletId] = useState(setBalance.wallet ?? setBalance.walletFrom ?? setBalance.walletTo);
    const [amount, setAmount] = useState(setBalance.value ?? setBalance.valueFrom ?? setBalance.valueTo);
    const [errors, setErrors] = useState(DEFAULT_ERROR);
    const amountInputRef = useRef(null);
    const wallet = useSelector((state) => selectWalletById(state, walletId));
    const currencies = useSelector(selectCurrencies) || [];
    const currency = wallet?.currency != null
        ? currencies.find((c) => c.id === wallet.currency || c.id == wallet.currency)
        : null;


    useEffect(() => {
        const dateValue = setBalance.date ? DateService.getDate(setBalance.date) : new Date();
        const hours = String(dateValue.getHours()).padStart(2, "0");
        const minutes = String(dateValue.getMinutes()).padStart(2, "0");
        const localDate =
            dateValue.getFullYear() +
            "-" +
            String(dateValue.getMonth() + 1).padStart(2, "0") +
            "-" +
            String(dateValue.getDate()).padStart(2, "0");

        setTransactionDate(localDate);
        setTransactionTime(`${hours}:${minutes}`);
        setDescription(setBalance.description);
        setWalletId(setBalance.wallet ?? setBalance.walletFrom ?? setBalance.walletTo);
        setAmount(setBalance.value ?? setBalance.valueFrom ?? setBalance.valueTo);

        setErrors(DEFAULT_ERROR);
    }, [setBalance, resetTrigger]);


    // const handleNameChanged = (newName) => {
    //     setName(newName);
    //     setErrors({...errors, name: null})
    // }

    const handleSave = () => {
        if (validateForm()) {
            const setBalanceToSave = {
                ...setBalance, description: description,
                date: transactionDate + 'T' + transactionTime + ':00',
                wallet: walletId,
                value: amount
            }
            return new Promise((resolve) => {
                dispatch(saveSetBalance(setBalanceToSave, () => resolve("close")));
            });
        }
    }

    const onDelete = () => {
        if (!setBalance.id) return;
        dispatch(deleteSetBalance(setBalance, () => onCancel()));
    }

    const validateForm = () => {
        const newErrors = {
            wallet: !walletId ? "Wallet is required" : null,
            amount: amount === null || amount === undefined || `${amount}`.trim() === "" ? "Amount is required" : null,
            date: !transactionDate ? "Date is required" : null,
            time: !transactionTime ? "Time is required" : null
        };
        setErrors(newErrors);
        return !Object.values(newErrors).some(Boolean);
    }

    const onAmountFocus = () => {
        const input = amountInputRef.current;
        if (input && typeof input.select === 'function') {
            setTimeout(() => input.select(), 0);
        }
    }

    const viewContent = (
        <>
            <CardContent>
                <Box sx={{ m: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t("common.labels.date")}</Typography>
                    <Typography variant="body1">{transactionDate} {transactionTime}</Typography>
                </Box>
                <Box sx={{ m: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t("common.labels.amount")}</Typography>
                    <Typography variant="body1">
                        {amount ?? "—"}{currency?.shortName ? " " + currency.shortName : ""}
                    </Typography>
                </Box>
                <Box sx={{ m: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t("common.labels.description")}</Typography>
                    <Typography variant="body1">{description || "—"}</Typography>
                </Box>
            </CardContent>
            <CardActions sx={{ ml: "2rem", mb: "1rem", mt: "1rem", mr: "2rem", height: "4rem" }}>
                <Box sx={{ position: "relative", width: "100%" }}>
                    <Box sx={{ position: "absolute", left: 0 }}>
                        <Button variant="outlined" sx={{ mr: "1rem" }} onClick={onCancel}>{t("common.buttons.close")}</Button>
                    </Box>
                </Box>
            </CardActions>
        </>
    );

    const editContent = ({ onCancel: handleCancel, onSave: handleSaveLayout }) => (
        <>
            <CardContent>
                <Box sx={{ m: 2 }}>
                    <TextField
                        id="date"
                        label={t("common.labels.date")}
                        type="date"
                        value={transactionDate}
                        onChange={e => {
                            setTransactionDate(e.target.value);
                            setErrors(prev => ({ ...prev, date: null }));
                        }}
                        error={!!errors.date}
                        helperText={errors.date}
                        sx={{ width: "50%" }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    <TextField
                        id="time"
                        label={t("common.labels.time")}
                        type="time"
                        value={transactionTime}
                        onChange={e => {
                            setTransactionTime(e.target.value);
                            setErrors(prev => ({ ...prev, time: null }));
                        }}
                        error={!!errors.time}
                        helperText={errors.time}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        inputProps={{
                            step: 300, // 5 min
                        }}
                        sx={{ width: "45%", marginLeft: "12px" }}
                    />
                </Box>
                <Box sx={{ m: 2 }}>
                    <TextField
                        label={t("common.labels.amount")}
                        variant="standard"
                        sx={{ width: "48%" }}
                        value={amount}
                        onChange={(event) => {
                            setAmount(event.target.value);
                            setErrors(prev => ({ ...prev, amount: null }));
                        }}
                        error={!!errors.amount}
                        helperText={errors.amount}
                        inputRef={amountInputRef}
                        InputProps={{
                            inputComponent: BalanceNumericFormat,
                            endAdornment: currency?.shortName ? (
                                <InputAdornment position="end">
                                    <Typography variant="body2" color="text.secondary">
                                        {currency.shortName}
                                    </Typography>
                                </InputAdornment>
                            ) : undefined,
                        }}
                        inputProps={{
                            onFocus: onAmountFocus,
                            onClick: onAmountFocus,
                        }}
                    />
                </Box>
                <Box sx={{ m: 2 }}>
                    <TextField id="description" multiline={true} maxRows={4} minRows={2} value={description}
                               onChange={(e) => setDescription(e.target.value)}
                               label={t("common.labels.description")} variant="standard" fullWidth={true} />
                </Box>
            </CardContent>
            <CardActions sx={{ ml: "2rem", mb: "1rem", mt: "1rem", mr: "2rem", height: "4rem" }}>
                <Box sx={{ position: "relative", width: "100%" }}>
                    <Box sx={{ position: "absolute", left: 0 }}>
                        <Button variant="outlined" sx={{ mr: "1rem" }} onClick={handleCancel}>{t("common.buttons.cancel")}</Button>
                        <Button variant="contained" onClick={handleSaveLayout}>{t("common.buttons.save")}</Button>
                    </Box>
                    {setBalance.id && (
                        <Box sx={{ position: "absolute", right: 0 }}>
                            <Button variant="outlined" color="error" onClick={onDelete}><Delete /></Button>
                        </Box>
                    )}
                </Box>
            </CardActions>
        </>
    );

    const isDraft = setBalance?.state === "DRAFT";

    return (
        <ViewAndEditFormLayout
            editTitle={t("forms.setBalance.editTitle")}
            viewTitle={t("common.labels.balance")}
            initialEdit={!setBalance.id || isDraft}
            onCancel={onCancel}
            onSave={handleSave}
            onDelete={setBalance.id ? onDelete : undefined}
            viewContent={setBalance.id ? viewContent : null}
            editContent={editContent}
        />
    );
}

export default EditSetBalanceForm